import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Card, CardContent } from '@/Components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import {
    Zap,
    Wrench,
    FileText,
    Shield,
    Plus,
    ExternalLink,
    Calendar
} from 'lucide-react';
import { format } from 'date-fns';

export default function AdminCustomerDetails({ customer, open, onOpenChange }) {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('ups');

    // Forms State
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({});
    const [selectedUPS, setSelectedUPS] = useState(null);

    // --- Queries ---
    const { data: upsList = [] } = useQuery({
        queryKey: ['adminCustomerUPS', customer?.email],
        queryFn: () => base44.entities.UPS.filter({ cliente_email: customer?.email }), // Removed sort to avoid 400 if col missing
        enabled: !!customer?.email
    });

    const { data: riparazioniList = [] } = useQuery({
        queryKey: ['adminCustomerRiparazioni', customer?.email],
        queryFn: () => base44.entities.Riparazione.filter({ cliente_email: customer?.email }),
        enabled: !!customer?.email
    });

    const { data: documentiList = [] } = useQuery({
        queryKey: ['adminCustomerDocumenti', customer?.email],
        queryFn: () => base44.entities.Documento.filter({ cliente_email: customer?.email }),
        enabled: !!customer?.email
    });

    const { data: contrattiList = [] } = useQuery({
        queryKey: ['adminCustomerContratti', customer?.email],
        queryFn: () => base44.entities.ContrattoManutenzione.filter({ cliente_email: customer?.email }),
        enabled: !!customer?.email
    });

    // --- Mutations ---
    const createMutation = useMutation({
        mutationFn: async ({ entity, data, entityName }) => {
            // 1. Create the entity
            const createdItem = await base44.entities[entity].create({
                ...data,
                cliente_email: customer.email
                // created_at: new Date().toISOString() // Removed to let DB handle default and avoid 400
            });

            // 2. Create Notification
            const title = `Nuovo ${entityName} aggiunto`;
            const message = `È stato aggiunto un nuovo ${entityName} al tuo account.`;

            await base44.entities.Notifica.create({
                cliente_email: customer.email,
                tipo: 'nuova_attivita',
                titolo: title,
                messaggio: message,
                link: '/area-clienti' // Generic link
            });

            // 3. Send Email
            await base44.integrations.Core.SendEmail({
                to: customer.email,
                subject: `Tecnosys - ${title}`,
                body: message
            });

            return createdItem;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [`adminCustomer${variables.entityName}`] });
            setShowAddForm(false);
            setFormData({});
            // Optional: Show success toast
            alert(`${variables.entityName} aggiunto con successo!`);
        }
    });

    const handleSubmit = (e, entity, entityName) => {
        e.preventDefault();
        createMutation.mutate({ entity, data: formData, entityName });
    };

    if (!customer) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white p-4 sm:p-6">
                <DialogHeader>
                    <DialogTitle>{customer.full_name || customer.email}</DialogTitle>
                    <DialogDescription>
                        Gestione dettagli per {customer.email}
                    </DialogDescription>
                </DialogHeader>

                <Tabs
                    value={activeTab}
                    onValueChange={(val) => {
                        setActiveTab(val);
                        setShowAddForm(false);
                        setFormData({});
                        setSelectedUPS(null);
                    }}
                    className="mt-4"
                >
                    <TabsList className="bg-white border w-full h-auto flex flex-wrap justify-start gap-1 p-1">
                        <TabsTrigger value="ups" className="gap-2 flex-1 min-w-[100px]"><Zap className="w-4 h-4" /> UPS</TabsTrigger>
                        <TabsTrigger value="riparazioni" className="gap-2 flex-1 min-w-[100px]"><Wrench className="w-4 h-4" /> Riparazioni</TabsTrigger>
                        <TabsTrigger value="documenti" className="gap-2 flex-1 min-w-[100px]"><FileText className="w-4 h-4" /> Documenti</TabsTrigger>
                        <TabsTrigger value="contratti" className="gap-2 flex-1 min-w-[100px]"><Shield className="w-4 h-4" /> Contratti</TabsTrigger>
                    </TabsList>

                    {/* UPS TAB */}
                    <TabsContent value="ups" className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">UPS Registrati</h3>
                            <Button onClick={() => { setShowAddForm(!showAddForm); setFormData({}); }} size="sm">
                                <Plus className="w-4 h-4 mr-2" /> Aggiungi UPS
                            </Button>
                        </div>

                        {showAddForm && (
                            <Card>
                                <CardContent className="pt-6">
                                    <form onSubmit={(e) => handleSubmit(e, 'UPS', 'UPS')} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label>Marca</Label>
                                                <Input value={formData.marca || ''} onChange={e => setFormData({ ...formData, marca: e.target.value })} required />
                                            </div>
                                            <div>
                                                <Label>Modello</Label>
                                                <Input value={formData.modello || ''} onChange={e => setFormData({ ...formData, modello: e.target.value })} required />
                                            </div>
                                            <div>
                                                <Label>Matricola</Label>
                                                <Input value={formData.matricola || ''} onChange={e => setFormData({ ...formData, matricola: e.target.value })} required />
                                            </div>
                                            <div>
                                                <Label>Data Installazione</Label>
                                                <Input type="date" value={formData.data_installazione || ''} onChange={e => setFormData({ ...formData, data_installazione: e.target.value })} required />
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>Annulla</Button>
                                            <Button type="submit" disabled={createMutation.isPending}>Salva</Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        <div className="space-y-2">
                            {upsList.map(item => (
                                <div
                                    key={item.id}
                                    className="flex justify-between items-center p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                                    onClick={() => setSelectedUPS(item)}
                                >
                                    <div>
                                        <p className="font-medium">{item.marca} {item.modello}</p>
                                        <p className="text-sm text-gray-500">S/N: {item.matricola} - Inst: {item.data_installazione}</p>
                                    </div>
                                    <Badge variant="outline">{item.ubicazione || 'Sede'}</Badge>
                                </div>
                            ))}
                            {upsList.length === 0 && !showAddForm && <p className="text-gray-500 text-center py-4">Nessun UPS registrato.</p>}
                        </div>

                        {/* UPS Detail Dialog */}
                        <Dialog open={!!selectedUPS} onOpenChange={(open) => !open && setSelectedUPS(null)}>
                            <DialogContent className="bg-white max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Dettagli UPS</DialogTitle>
                                </DialogHeader>
                                {selectedUPS && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-xs text-gray-500">Marca</Label>
                                                <p className="font-medium">{selectedUPS.marca}</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500">Modello</Label>
                                                <p className="font-medium">{selectedUPS.modello}</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500">Matricola</Label>
                                                <p className="font-medium">{selectedUPS.matricola}</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500">Data Installazione</Label>
                                                <p>{selectedUPS.data_installazione}</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500">Potenza</Label>
                                                <p>{selectedUPS.potenza_va ? `${selectedUPS.potenza_va} VA` : '-'}</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500">Ubicazione</Label>
                                                <p>{selectedUPS.ubicazione || '-'}</p>
                                            </div>
                                        </div>
                                        {selectedUPS.note && (
                                            <div>
                                                <Label className="text-xs text-gray-500">Note</Label>
                                                <p className="text-sm bg-gray-50 p-2 rounded">{selectedUPS.note}</p>
                                            </div>
                                        )}
                                        <div className="flex justify-end pt-4">
                                            <Button variant="outline" onClick={() => setSelectedUPS(null)}>Chiudi</Button>
                                        </div>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>
                    </TabsContent>

                    {/* RIPARAZIONI TAB */}
                    <TabsContent value="riparazioni" className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Storico Riparazioni</h3>
                            <Button onClick={() => { setShowAddForm(!showAddForm); setFormData({ stato_corrente: 'ricevuto' }); }} size="sm">
                                <Plus className="w-4 h-4 mr-2" /> Nuova Riparazione
                            </Button>
                        </div>

                        {showAddForm && (
                            <Card>
                                <CardContent className="pt-6">
                                    <form onSubmit={(e) => handleSubmit(e, 'Riparazione', 'Riparazioni')} className="space-y-4">
                                        <div>
                                            <Label>UPS Correlato</Label>
                                            <Select onValueChange={(v) => setFormData({ ...formData, ups_id: v })} required>
                                                <SelectTrigger><SelectValue placeholder="Seleziona UPS" /></SelectTrigger>
                                                <SelectContent>
                                                    {upsList.map(u => (
                                                        <SelectItem key={u.id} value={u.id}>{u.marca} {u.modello} ({u.matricola})</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Descrizione Problema</Label>
                                            <Textarea value={formData.descrizione_problema || ''} onChange={e => setFormData({ ...formData, descrizione_problema: e.target.value })} required />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>Annulla</Button>
                                            <Button type="submit">Crea Ticket</Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        <div className="space-y-2">
                            {riparazioniList.map(item => (
                                <div key={item.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium">Ticket #{item.id.slice(0, 8)}</p>
                                            <Badge>{item.stato_corrente}</Badge>
                                        </div>
                                        <p className="text-sm text-gray-600 truncate max-w-md">{item.descrizione_problema}</p>
                                    </div>
                                    <span className="text-xs text-gray-500">{format(new Date(item.created_at), 'dd/MM/yyyy')}</span>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* DOCUMENTI TAB */}
                    <TabsContent value="documenti" className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Documenti Condivisi</h3>
                            <Button onClick={() => { setShowAddForm(!showAddForm); setFormData({}); }} size="sm">
                                <Plus className="w-4 h-4 mr-2" /> Carica Documento
                            </Button>
                        </div>

                        {showAddForm && (
                            <Card>
                                <CardContent className="pt-6">
                                    <form onSubmit={(e) => handleSubmit(e, 'Documento', 'Documenti')} className="space-y-4">
                                        <div>
                                            <Label>Titolo</Label>
                                            <Input value={formData.titolo || ''} onChange={e => setFormData({ ...formData, titolo: e.target.value })} required />
                                        </div>
                                        <div>
                                            <Label>Tipo</Label>
                                            <Select onValueChange={(v) => setFormData({ ...formData, tipo: v })} required>
                                                <SelectTrigger><SelectValue placeholder="Seleziona Tipo" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="fattura">Fattura</SelectItem>
                                                    <SelectItem value="preventivo">Preventivo</SelectItem>
                                                    <SelectItem value="report">Report Tecnico</SelectItem>
                                                    <SelectItem value="manuale">Manuale</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>URL File (Link pubblico)</Label>
                                            <Input value={formData.file_url || ''} onChange={e => setFormData({ ...formData, file_url: e.target.value })} placeholder="https://..." required />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>Annulla</Button>
                                            <Button type="submit">Salva</Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        <div className="space-y-2">
                            {documentiList.map(item => (
                                <div key={item.id} className="flex justify-between items-center p-3 border rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileText className="text-blue-500" />
                                        <div>
                                            <p className="font-medium">{item.titolo}</p>
                                            <p className="text-xs uppercase text-gray-500">{item.tipo}</p>
                                        </div>
                                    </div>
                                    <a href={item.file_url} target="_blank" rel="noreferrer">
                                        <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* CONTRATTI TAB */}
                    <TabsContent value="contratti" className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Contratti Attivi</h3>
                            <Button onClick={() => { setShowAddForm(!showAddForm); setFormData({ stato: 'attivo' }); }} size="sm">
                                <Plus className="w-4 h-4 mr-2" /> Nuovo Contratto
                            </Button>
                        </div>

                        {showAddForm && (
                            <Card>
                                <CardContent className="pt-6">
                                    <form onSubmit={(e) => handleSubmit(e, 'ContrattoManutenzione', 'Contratti')} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label>Tipologia</Label>
                                                <Input value={formData.tipologia || ''} onChange={e => setFormData({ ...formData, tipologia: e.target.value })} required />
                                            </div>
                                            <div>
                                                <Label>UPS Serial Number (Opzionale)</Label>
                                                <Input value={formData.ups_serial_number || ''} onChange={e => setFormData({ ...formData, ups_serial_number: e.target.value })} />
                                            </div>
                                            <div>
                                                <Label>Data Inizio</Label>
                                                <Input type="date" value={formData.data_inizio || ''} onChange={e => setFormData({ ...formData, data_inizio: e.target.value })} required />
                                            </div>
                                            <div>
                                                <Label>Data Fine</Label>
                                                <Input type="date" value={formData.data_fine || ''} onChange={e => setFormData({ ...formData, data_fine: e.target.value })} required />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Dettagli</Label>
                                            <Textarea value={formData.dettagli || ''} onChange={e => setFormData({ ...formData, dettagli: e.target.value })} />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>Annulla</Button>
                                            <Button type="submit">Crea Contratto</Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        <div className="space-y-2">
                            {contrattiList.map(item => (
                                <div key={item.id} className="p-4 border rounded-lg bg-white shadow-sm">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="font-bold text-lg">{item.tipologia}</h4>
                                            <p className="text-sm text-gray-500">UPS: {item.ups_serial_number || 'Tutti'}</p>
                                        </div>
                                        <Badge className={item.stato === 'attivo' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>{item.stato}</Badge>
                                    </div>
                                    <div className="flex gap-4 text-sm text-gray-600 mt-2">
                                        <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Dal: {format(new Date(item.data_inizio), 'dd/MM/yyyy')}</div>
                                        <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Al: {format(new Date(item.data_fine), 'dd/MM/yyyy')}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function Badge({ children, variant = 'default', className = '' }) {
    const baseClass = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground border border-gray-200",
    };
    return <div className={`${baseClass} ${variants[variant] || variants.default} ${className}`}>{children}</div>;
}
